import { useCallback, createContext } from "react";
import {
  GIS_PM_POINTS_FIXED_ID,
  GIS_PM_POINTS_SUPPLEMENTARY_ID,
  GIS_PM_POLYGONS_ID,
  GIS_PM_POLYGONS_COMP_ID,
  GIS_PM_ROADS_ID,
  GIS_PM_ROADS_COMP_ID,
  ARCGIS_SERVICES_ROOT_URL,
  ACTIVE_HIGHLIGHT_LINE_ID,
  GIS_PM_LAYER_NAME,
} from "../settings";
import { geoProcessor } from "../hooks/useEsriLoader";

export const EsriContext = createContext();

export const EsriProvider = ({ esriLoader, children }) => {
  const setLayerVisible = useCallback(
    (layerId, visible) => {
      if (layerId || esriLoader.view) {
        const getFeature = esriLoader.view.map.findLayerById(layerId);
        if (getFeature) {
          esriLoader.view.whenLayerView(getFeature).then(function (layerView) {
            layerView.visible = visible;
          });
        }
      }
    },
    [esriLoader]
  );

  const addLayer = useCallback(
    (options) => {
      if (!options) return;
      const FeatureLayer = new esriLoader.FeatureLayer(options);
      esriLoader.view.map.add(FeatureLayer);
    },
    [esriLoader]
  );

  const getFeatureLayer = useCallback(
    (layerId) => {
      if (!layerId) return;
      return new Promise((resolve, reject) => {
        const getFeature = esriLoader.view.map.findLayerById(layerId);
        if (getFeature) {
          getFeature.load().then((layer) => {
            resolve(layer);
          });
        } else {
          reject({ loadStatus: "none" });
        }
      });
    },
    [esriLoader]
  );

  const removeLayer = useCallback(
    (layerId) => {
      if (!layerId) {
        return;
      }
      const getFeature = esriLoader.view.map.findLayerById(layerId);
      if (getFeature) {
        esriLoader.view.map.remove(getFeature);
      }
    },
    [esriLoader]
  );

  const removeAllDataLayers = useCallback(() => {
    removeLayer(GIS_PM_POINTS_SUPPLEMENTARY_ID);
    removeLayer(GIS_PM_POINTS_FIXED_ID);
    removeLayer(GIS_PM_ROADS_ID);
    removeLayer(GIS_PM_ROADS_COMP_ID);
    removeLayer(GIS_PM_POLYGONS_ID);
    removeLayer(GIS_PM_POLYGONS_COMP_ID);
    removeLayer(ACTIVE_HIGHLIGHT_LINE_ID); // active highlight feature layer
  }, [removeLayer]);

  const layerOpacity = useCallback(
    (layerId, opacityValue) => {
      if (!esriLoader) return;
      const getFeature = esriLoader.view.map.findLayerById(layerId);
      if (getFeature) {
        if (opacityValue >= 0 && opacityValue <= 1) {
          getFeature.opacity = opacityValue;
        }
      }
    },
    [esriLoader]
  );

  const getComplianceStatus = useCallback(
    async (featureUrl, uniqueCode) => {
      if (!featureUrl || !uniqueCode) return;
      const FeatureLayer = new esriLoader.FeatureLayer({
        url: featureUrl,
      });
      const query = FeatureLayer.createQuery();
      let buildQuery = {
        ...query,
        where: "unique_code='" + uniqueCode + "'",
        returnGeometry: false,
        returnExtentOnly: false,
        outFields: ["compliance_status"],
      };
      return new Promise((resolve, reject) => {
        FeatureLayer.load().then(() => {
          FeatureLayer.queryFeatures(buildQuery).then((response) => {
            if (response.features[0]) {
              resolve(response.features[0]);
            }
          });
        });
      });
    },
    [esriLoader]
  );

  const navigateAndDisplayPopup = useCallback(
    async (featureUrl, uniqueCode) => {
      if (!featureUrl || !uniqueCode) return;
      const getPopupFeature =
        esriLoader.view.map.findLayerById(GIS_PM_LAYER_NAME);
      const FeatureLayer = new esriLoader.FeatureLayer({
        url: featureUrl,
      });
      const query = FeatureLayer.createQuery();
      let buildQuery = {
        ...query,
        where: "unique_code='" + uniqueCode + "'",
        returnGeometry: true,
        returnExtentOnly: true,
        outFields: ["*"],
      };
      return new Promise((resolve, reject) => {
        FeatureLayer.load().then(() => {
          FeatureLayer.queryFeatures(buildQuery).then((response) => {
            if (response.features[0]) {
              removeAllDataLayers();
              // navigate to area
              esriLoader.view.goTo(response.features[0]).then(() => {
                // query current layer to load the popup
                getPopupFeature?.load().then(() => {
                  getPopupFeature
                    .queryFeatures(buildQuery)
                    .then((f_response) => {
                      esriLoader.view.popup.open({
                        location: f_response.features[0].geometry.extent.center,
                        features: [f_response.features[0]],
                      });
                      resolve(uniqueCode);
                    });
                });
              });
            }
          });
        });
      });
    },
    [esriLoader, removeAllDataLayers]
  );

  const highlightFeatureItem = useCallback(
    (featureUrl, uniqueCode) => {
      if (!featureUrl || !uniqueCode) return;
      removeLayer(ACTIVE_HIGHLIGHT_LINE_ID);
      const buildQuery = {
        where: "unique_code='" + uniqueCode + "'",
        returnGeometry: true,
        returnExtentOnly: true,
        outFields: ["*"],
      };
      const borderRenderer = {
        type: "simple",
        symbol: {
          type: "simple-line",
          width: "2px",
          color: [0, 0, 0],
        },
      };
      new esriLoader.FeatureLayer({
        url: featureUrl,
        outFields: ["*"],
        returnExtentOnly: true,
      })
        .load()
        .then((layer) => {
          return layer.queryFeatures(buildQuery).then((featureSet) => {
            if (featureSet.features) {
              return new esriLoader.FeatureLayer({
                id: ACTIVE_HIGHLIGHT_LINE_ID,
                source: featureSet.features,
                fields: layer.fields,
                renderer: borderRenderer,
                legendEnabled: false,
                outFields: ["*"],
              }).load();
            }
          });
        })
        .then((layer) => {
          esriLoader.view.map.add(layer);
        })
        .catch((error) => {
          console.error(error);
        });
    },
    [esriLoader, removeLayer]
  );

  const rendererStyles = useCallback(
    async ({
      layerServiceURL,
      uniqueCode,
      layerUniqueId,
      layerOptions,
      layerName,
    }) => {
      if (!layerServiceURL || !uniqueCode) return;
      const query = {
        where: uniqueCode, // Set by select element
        spatialRelationship: "intersects", // Relationship operation to apply
        outFields: ["*"], // Attributes to return
        returnGeometry: true,
      };
      return new Promise((resolve, reject) => {
        new esriLoader.FeatureLayer({
          url: layerServiceURL,
          outFields: ["*"],
          returnExtentOnly: true,
          ...layerOptions,
        })
          .load()
          .then((layer) => {
            return layer.queryFeatures(query).then((featureSet) => {
              const getUnit = featureSet.features[0]["attributes"]["unit"];
              return new esriLoader.FeatureLayer({
                id: layerUniqueId ? layerUniqueId : "_d",
                title: `${layerName} (${getUnit ? getUnit : ""})`,
                source: featureSet.features,
                fields: layer.fields,
                renderer: layer.renderer,
                outFields: ["*"],
                ...layerOptions,
              }).load();
            });
          })
          .then((layer) => {
            esriLoader.view.map.add(layer);
            resolve("done");
          })
          .catch((error) => {
            console.error(error);
            reject(error);
          });
      });
    },
    [esriLoader]
  );

  const queryLayer = useCallback(
    async (featureUrl, queryParams) => {
      if (!featureUrl || !queryParams) return;
      const FeatureLayer = new esriLoader.FeatureLayer({
        url: featureUrl,
      });
      const query = FeatureLayer.createQuery();
      let buildQuery = { ...query, ...queryParams };

      return new Promise((resolve, reject) => {
        FeatureLayer.load()
          .then(() => {
            return FeatureLayer.queryFeatures(buildQuery)
              .then((response) => {
                const _returnToCallback = response.features.map((item) => {
                  return {
                    name: item.attributes["name"],
                    id: item.attributes["unique_code"],
                    ...item.attributes,
                  };
                });
                resolve(_returnToCallback);
              })
              .catch((e) => {
                reject(e);
              });
          })
          .catch((e) => {
            reject(e);
          });
      });
    },
    [esriLoader]
  );

  const registerToken = useCallback(
    (tokenJSON) => {
      if (!tokenJSON) return;
      esriLoader.IdentityManager.registerToken(tokenJSON);
    },
    [esriLoader]
  );

  const destroyToken = useCallback(() => {
    const credential = esriLoader.IdentityManager.findCredential(
      ARCGIS_SERVICES_ROOT_URL
    );
    if (credential) {
      esriLoader.IdentityManager.destroyCredentials();
      credential.destroy();
    }
  }, [esriLoader]);

  const resetMapZoom = useCallback(() => {
    esriLoader.view.zoom = 7;
  }, [esriLoader]);

  const triggerPopupActions = useCallback(
    (actionID, callback) => {
      if (!esriLoader) return;
      esriLoader.view.popup.autoCloseEnabled = true;
      esriLoader.view.popup.on("trigger-action", function (event) {
        if (event.action.id === actionID) {
          const { attributes } = esriLoader.view.popup.selectedFeature;
          callback(attributes);
        }
      });
    },
    [esriLoader]
  );

  const clearPopup = useCallback(() => {
    esriLoader.view.popup.close();
  }, [esriLoader]);

  const onViewLoaded = useCallback(
    (callback) => {
      if (!esriLoader) return;
      esriLoader.view.when((evt) => {
        callback(evt);
      });
    },
    [esriLoader]
  );

  const methods = {
    setLayerVisible,
    addLayer,
    removeLayer,
    queryLayer,
    navigateAndDisplayPopup,
    rendererStyles,
    geoProcessor,
    removeAllDataLayers,
    resetMapZoom,
    esriLoader,
    registerToken,
    destroyToken,
    layerOpacity,
    getFeatureLayer,
    highlightFeatureItem,
    triggerPopupActions,
    onViewLoaded,
    getComplianceStatus,
    clearPopup,
  };

  return (
    <EsriContext.Provider value={methods}>{children}</EsriContext.Provider>
  );
};
